import { useEffect, useState } from 'react';

import { CAMPO_DE_BUSCA, FORM_DE_BUSCA } from 'components/reconhecimento-ui';
import BuscaDePessoas from 'components/ui/busca-de-pessoas';
import Button from 'components/ui/button';
import InfoTooltip from 'components/ui/info-tooltip';
import Paginacao from 'components/ui/paginacao';
import { DESTAQUES_POR_PAGINA, DestaqueDoFunci, getDestaquesDoPrefixo } from 'services/destaques.service';

import { useInicioDosResultados } from '../paginacao-dos-resultados';
import CartaoDoFunci from './CartaoDoFunci';
import ExplicacaoDosDestaques from './ExplicacaoDosDestaques';

interface PrefixoDeQuemAcessa {
  prefixo?: number | string;
  dependencia?: string;
}

interface ListaDeDestaquesProps {
  ciclo: { id: string; titulo: string };
  onAbrir: (funci: DestaqueDoFunci) => void;
  /** Prefixo e dependência de quem acessa (`userInfo`, do `player_fields`), para o topo mesmo com a lista vazia. */
  prefixoDeQuemAcessa?: PrefixoDeQuemAcessa;
}

type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'pronto'; itens: DestaqueDoFunci[]; total: number };

const numero = (valor: number) => valor.toLocaleString('pt-BR');

/**
 * Lista de Destaques (`Galeria.dc.html`): o topo azul com o total do ciclo, a busca e a grade de funcis do
 * prefixo de quem acessa, paginada pelo servidor (DST-01, DST-05 a DST-11).
 *
 * Contraste: o prefixo e a dependência do topo vão em branco negrito (o amarelo do design dava 4,44:1 em 14 px) e
 * os textos do topo em branco pleno.
 */
export default function ListaDeDestaques({ ciclo, onAbrir, prefixoDeQuemAcessa }: ListaDeDestaquesProps) {
  const [pagina, setPagina] = useState(1);
  const [buscaEnviada, setBuscaEnviada] = useState('');
  const [tentativa, setTentativa] = useState(0);
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  // O topo mostra o total do ciclo; a busca muda só o total da seção.
  const [totalDoCiclo, setTotalDoCiclo] = useState<number>();
  // Sem o `userInfo` (ainda carregando ou com falha), o prefixo vem com os itens: o `user.extra` não o tem.
  const [prefixoDosItens, setPrefixoDosItens] = useState<PrefixoDeQuemAcessa>();

  const { ref: inicioDosResultados, irParaOInicio } = useInicioDosResultados(carga.estado === 'carregando');

  useEffect(() => {
    let atual = true;
    setCarga({ estado: 'carregando' });

    getDestaquesDoPrefixo({ cycleId: ciclo.id, pagina, busca: buscaEnviada })
      .then(({ itens, total }) => {
        if (!atual) return;
        setCarga({ estado: 'pronto', itens, total });
        if (!buscaEnviada) setTotalDoCiclo(total);
        if (itens[0]?.prefixo) setPrefixoDosItens({ prefixo: itens[0].prefixo, dependencia: itens[0].nome_dependencia });
      })
      .catch(() => {
        if (atual) setCarga({ estado: 'erro' });
      });

    return () => {
      atual = false;
    };
  }, [ciclo.id, pagina, buscaEnviada, tentativa]);

  function buscar(termo: string) {
    setPagina(1);
    setBuscaEnviada(termo);
  }

  function irParaPagina(indice: number) {
    setPagina(indice + 1);
    irParaOInicio();
  }

  const pronto = carga.estado === 'pronto' ? carga : undefined;
  const { prefixo, dependencia } = prefixoDeQuemAcessa?.prefixo ? prefixoDeQuemAcessa : (prefixoDosItens ?? {});
  const totalDePaginas = pronto ? Math.ceil(pronto.total / DESTAQUES_POR_PAGINA) : 0;

  return (
    <div className="flex flex-col gap-[22px]">
      <section className="flex flex-wrap items-center gap-x-10 gap-y-5 rounded-[20px] bg-brand-blue px-[30px] py-[26px] text-brand-white">
        <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-2.5">
          <h2 className="m-0 font-display text-[32px] font-extrabold leading-[1.05] [text-wrap:balance]">
            Quem <span className="text-brand-yellow">#JogaJunto</span> aparece aqui.
          </h2>
          <p className="m-0 text-sm leading-[1.55] text-brand-white">
            Funcis do {prefixo ? 'prefixo ' : 'seu prefixo'}
            {prefixo && <b className="font-bold text-brand-white">{[prefixo, dependencia].filter(Boolean).join(' · ')}</b>} que
            receberam destaque no ciclo. Cada funci aparece uma vez, com todos os destaques que conquistou.
          </p>
        </div>
        {totalDoCiclo !== undefined && (
          <div className="shrink-0">
            <b className="font-display text-[30px] font-extrabold leading-none text-brand-yellow">{numero(totalDoCiclo)}</b>
            <span className="mt-[3px] block text-xs text-brand-white">funcis com destaque</span>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-7 rounded-3xl bg-surface-0 px-[26px] pb-7 pt-6 shadow-jj-01">
        <div ref={inicioDosResultados} tabIndex={-1} className="flex scroll-mt-[140px] flex-wrap items-center gap-4 outline-none">
          <div className="min-w-[260px] flex-1">
            {/* DEX-01: o (i) explica os três destaques, no balão da nova identidade (o da versão atual era o da Cultura). */}
            <div className="group/tip relative flex items-center gap-2">
              <h2 className="m-0 font-display text-[22px] font-bold leading-tight text-brand-blue-dark">
                Funcis com destaque{pronto && <span className="text-brand-blue"> ({numero(pronto.total)})</span>}
              </h2>
              {/* Abre abaixo: o texto é longo e, acima, era cortado no alto da tela. */}
              <InfoTooltip rotulo="Como funcionam os destaques" largura="w-[min(380px,calc(100vw-72px))]" abrePara="baixo">
                <ExplicacaoDosDestaques ciclo={ciclo.titulo} />
              </InfoTooltip>
            </div>
            <p className="m-0 mt-1.5 text-[13px] text-content">Clique em um funci para ver a página dele.</p>
          </div>

          {/* As classes da busca das telas de reconhecimento: o campo em 16 px no celular evita o zoom do iOS no foco.
              A busca filtra ao digitar e no Enter, como nas outras telas (AJ2-58). */}
          <BuscaDePessoas
            rotulo="Pesquisar funci"
            placeholder="Nome, matrícula ou grupo"
            onBuscar={buscar}
            classeDoFormulario={`${FORM_DE_BUSCA} border-solid`}
            classeDoCampo={`${CAMPO_DE_BUSCA} font-text`}
            depoisDoCampo={
              <button
                type="submit"
                aria-label="Buscar"
                className="flex shrink-0 cursor-pointer border-0 bg-transparent p-0 text-brand-blue"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </button>
            }
          />
        </div>

        {carga.estado === 'carregando' && (
          <div>
            <span role="status" className="sr-only">
              Carregando os destaques
            </span>
            <div aria-hidden="true" className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map(indice => (
                <div key={indice} className="h-[300px] animate-pulse rounded-[18px] bg-surface-200 motion-reduce:animate-none" />
              ))}
            </div>
          </div>
        )}

        {carga.estado === 'erro' && (
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-surface-100 px-6 py-9 text-center">
            <p className="m-0 text-sm text-content">Não foi possível carregar os destaques.</p>
            <Button variant="secundario" size="sm" onClick={() => setTentativa(anterior => anterior + 1)}>
              Tentar de novo
            </Button>
          </div>
        )}

        {pronto && pronto.itens.length === 0 && (
          <div className="rounded-2xl bg-brand-yellow-light px-6 py-9 text-center">
            <p className="m-0 mb-1.5 font-display text-xl font-extrabold text-brand-blue-dark">Ninguém encontrado</p>
            <p className="m-0 text-sm text-content">
              {buscaEnviada ? 'Confira o nome ou a matrícula pesquisada.' : 'Ninguém do seu prefixo teve destaque neste ciclo.'}
            </p>
          </div>
        )}

        {pronto && pronto.itens.length > 0 && (
          <div>
            <ul className="m-0 grid list-none grid-cols-1 gap-[18px] p-0 sm:grid-cols-2 lg:grid-cols-3">
              {pronto.itens.map(funci => (
                <li key={funci._id} className="flex min-w-0 flex-col [&>button]:flex-1">
                  <CartaoDoFunci funci={funci} ciclo={ciclo.titulo} onAbrir={onAbrir} />
                </li>
              ))}
            </ul>
            <Paginacao total={totalDePaginas} atual={pagina - 1} onChange={irParaPagina} />
          </div>
        )}
      </section>
    </div>
  );
}
